// Pure, dependency-free GPX merger for the public /garmin-gpx tool.
//
// Many Garmin devices/apps only import the FIRST <trk> or <rte> from a GPX file
// (or choke entirely) when a file contains several of them — which is exactly
// the shape of our multi-day hike exports (one <trk>/<rte> per day). This module
// parses an arbitrary GPX string in the browser and re-emits it with every track
// or route flattened into a SINGLE <trk> (one <trkseg>) or SINGLE <rte>, so the
// whole multi-day route imports as one continuous course.
//
// Everything runs client-side: the file never leaves the user's machine.

export type GpxOutputType = 'trk' | 'rte';

export interface GpxPoint {
  lat: number;
  lon: number;
  ele?: number;
  time?: string; // ISO-8601, preserved verbatim from source
}

export interface GpxWaypoint extends GpxPoint {
  name?: string;
  desc?: string;
  sym?: string;
  type?: string;
}

export interface GpxSegment {
  name?: string;
  points: GpxPoint[];
}

export interface ParsedGpx {
  name?: string;
  metadataTime?: string;
  tracks: GpxSegment[];
  routes: GpxSegment[];
  waypoints: GpxWaypoint[];
}

export interface MergeOptions {
  // Force the output container type. When omitted, the merged output keeps the
  // same type as the source (tracks -> <trk>, routes -> <rte>); a file with both
  // defaults to <trk>.
  outputType?: GpxOutputType;
  // Name written to <metadata><name> and the output <trk>/<rte> <name>.
  name?: string;
  creator?: string;
}

export interface ConvertResult {
  gpx: string;
  outputType: GpxOutputType;
  // Counts describing the SOURCE file, for surfacing in the UI.
  sourceTrackCount: number;
  sourceRouteCount: number;
  waypointCount: number;
  mergedPointCount: number;
}

const GPX_NS = 'http://www.topografix.com/GPX/1/1';
const XSI_NS = 'http://www.w3.org/2001/XMLSchema-instance';
const SCHEMA_LOCATION =
  'http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd';

const DEFAULT_CREATOR = 'HiiKER — hiiker.app/garmin-gpx';

/* ------------------------------- parsing -------------------------------- */

// getElementsByTagNameNS('*', name) matches the element regardless of whether
// the GPX uses the default namespace, a prefix, or no namespace at all.
const childrenByTag = (parent: Element | Document, tag: string): Element[] =>
  Array.from(parent.getElementsByTagNameNS('*', tag));

// Direct children only (e.g. a <trk>'s own <name>, not a nested waypoint's).
const directChildText = (parent: Element, tag: string): string | undefined => {
  for (const child of Array.from(parent.children)) {
    if (child.localName === tag) return child.textContent?.trim() || undefined;
  }
  return undefined;
};

const parsePoint = (el: Element): GpxPoint | null => {
  const lat = parseFloat(el.getAttribute('lat') ?? '');
  const lon = parseFloat(el.getAttribute('lon') ?? '');
  if (Number.isNaN(lat) || Number.isNaN(lon)) return null;

  const point: GpxPoint = { lat, lon };

  const eleText = directChildText(el, 'ele');
  if (eleText !== undefined) {
    const ele = parseFloat(eleText);
    if (!Number.isNaN(ele)) point.ele = ele;
  }

  const timeText = directChildText(el, 'time');
  if (timeText) point.time = timeText;

  return point;
};

export class GpxParseError extends Error {}

export const parseGpx = (xml: string): ParsedGpx => {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');

  // DOMParser reports malformed XML via a <parsererror> node rather than throwing.
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new GpxParseError('This file could not be parsed as XML.');
  }

  const root = doc.documentElement;
  if (!root || root.localName !== 'gpx') {
    throw new GpxParseError("This doesn't look like a GPX file (no <gpx> root element).");
  }

  const metadata = childrenByTag(root, 'metadata')[0];
  const metadataName = metadata ? directChildText(metadata, 'name') : undefined;
  const metadataTime = metadata ? directChildText(metadata, 'time') : undefined;

  const tracks: GpxSegment[] = childrenByTag(root, 'trk').map((trk) => {
    const points = childrenByTag(trk, 'trkpt')
      .map(parsePoint)
      .filter((p): p is GpxPoint => p !== null);
    return { name: directChildText(trk, 'name'), points };
  });

  const routes: GpxSegment[] = childrenByTag(root, 'rte').map((rte) => {
    const points = childrenByTag(rte, 'rtept')
      .map(parsePoint)
      .filter((p): p is GpxPoint => p !== null);
    return { name: directChildText(rte, 'name'), points };
  });

  const waypoints: GpxWaypoint[] = childrenByTag(root, 'wpt')
    .map((wpt) => {
      const base = parsePoint(wpt);
      if (!base) return null;
      const wp: GpxWaypoint = { ...base };
      wp.name = directChildText(wpt, 'name');
      wp.desc = directChildText(wpt, 'desc');
      wp.sym = directChildText(wpt, 'sym');
      wp.type = directChildText(wpt, 'type');
      return wp;
    })
    .filter((w): w is GpxWaypoint => w !== null);

  return {
    name: metadataName || tracks.find((t) => t.name)?.name || routes.find((r) => r.name)?.name,
    metadataTime,
    tracks,
    routes,
    waypoints,
  };
};

/* ------------------------------ serializing ----------------------------- */

const esc = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

// Trim to a sane precision; keep full precision if the source had fewer digits.
const coord = (n: number): string => String(n);

const pointChildren = (p: GpxPoint, indent: string): string => {
  let out = '';
  if (p.ele !== undefined) out += `${indent}<ele>${coord(p.ele)}</ele>\n`;
  if (p.time) out += `${indent}<time>${esc(p.time)}</time>\n`;
  return out;
};

const resolveOutputType = (parsed: ParsedGpx, opts: MergeOptions): GpxOutputType => {
  if (opts.outputType) return opts.outputType;
  if (parsed.tracks.length > 0) return 'trk';
  if (parsed.routes.length > 0) return 'rte';
  return 'trk';
};

// All track points then all route points, in document order, flattened into one
// continuous list. For the common single-kind multi-day file this is just the
// days concatenated end-to-end.
const collectPoints = (parsed: ParsedGpx): GpxPoint[] => {
  const points: GpxPoint[] = [];
  for (const t of parsed.tracks) points.push(...t.points);
  for (const r of parsed.routes) points.push(...r.points);
  return points;
};

const serializeWaypoint = (w: GpxWaypoint): string => {
  let out = `  <wpt lat="${coord(w.lat)}" lon="${coord(w.lon)}">\n`;
  out += pointChildren(w, '    ');
  if (w.name) out += `    <name>${esc(w.name)}</name>\n`;
  if (w.desc) out += `    <desc>${esc(w.desc)}</desc>\n`;
  if (w.sym) out += `    <sym>${esc(w.sym)}</sym>\n`;
  if (w.type) out += `    <type>${esc(w.type)}</type>\n`;
  out += '  </wpt>\n';
  return out;
};

export const buildGpx = (parsed: ParsedGpx, opts: MergeOptions = {}): ConvertResult => {
  const outputType = resolveOutputType(parsed, opts);
  const points = collectPoints(parsed);
  const name = opts.name?.trim() || parsed.name || 'Merged route';
  const creator = opts.creator || DEFAULT_CREATOR;

  let body = '';

  body += '  <metadata>\n';
  body += `    <name>${esc(name)}</name>\n`;
  if (parsed.metadataTime) body += `    <time>${esc(parsed.metadataTime)}</time>\n`;
  body += '  </metadata>\n';

  for (const w of parsed.waypoints) body += serializeWaypoint(w);

  if (outputType === 'trk') {
    body += '  <trk>\n';
    body += `    <name>${esc(name)}</name>\n`;
    body += '    <trkseg>\n';
    for (const p of points) {
      body += `      <trkpt lat="${coord(p.lat)}" lon="${coord(p.lon)}">\n`;
      body += pointChildren(p, '        ');
      body += '      </trkpt>\n';
    }
    body += '    </trkseg>\n';
    body += '  </trk>\n';
  } else {
    body += '  <rte>\n';
    body += `    <name>${esc(name)}</name>\n`;
    for (const p of points) {
      body += `    <rtept lat="${coord(p.lat)}" lon="${coord(p.lon)}">\n`;
      body += pointChildren(p, '      ');
      body += '    </rtept>\n';
    }
    body += '  </rte>\n';
  }

  const header =
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<gpx version="1.1" creator="${esc(creator)}" ` +
    `xmlns="${GPX_NS}" ` +
    `xmlns:xsi="${XSI_NS}" ` +
    `xsi:schemaLocation="${SCHEMA_LOCATION}">\n`;

  const gpx = `${header}${body}</gpx>\n`;

  return {
    gpx,
    outputType,
    sourceTrackCount: parsed.tracks.length,
    sourceRouteCount: parsed.routes.length,
    waypointCount: parsed.waypoints.length,
    mergedPointCount: points.length,
  };
};

/* ------------------------------ public API ------------------------------ */

export const convertGpx = (xml: string, opts: MergeOptions = {}): ConvertResult => {
  const parsed = parseGpx(xml);
  if (parsed.tracks.length === 0 && parsed.routes.length === 0) {
    throw new GpxParseError('No tracks (<trk>) or routes (<rte>) were found in this file.');
  }
  return buildGpx(parsed, opts);
};
